import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';

import SystemTabs from '../../../ui/SystemTabs';
import Checkbox from '../../form/Checkbox';
import Select from '../../form/Select';

const TIPOS_DESTINATARIO = [
    {
        id: 'TODOS',
        descricao: 'TODOS',
    },
    {
        id: 'CONTRIBUINTE',
        descricao: 'CONTRIBUINTE',
    },
    {
        id: 'ISENTO',
        descricao: 'ISENTO',
    },
    {
        id: 'NAO_CONTRIBUINTE',
        descricao: 'NÃO CONTRIBUINTE',
    },
];

const CONSUMIDOR_FINAL = [
    {
        id: 'true',
        descricao: 'SIM',
    },
    {
        id: 'false',
        descricao: 'NÃO',
    },
];

function CampoDecimal({ name, label, value, onChange }) {
    return (
        <TextField
            name={name}
            label={label}
            type="number"
            value={value ?? ''}
            onChange={onChange}
            size="small"
            inputProps={{
                min: 0,
                step: '0.0001',
            }}
            sx={{ width: 210 }}
        />
    );
}

function CampoCodigo({ name, label, value, onChange, maxLength, width = 180 }) {
    return (
        <TextField
            name={name}
            label={label}
            value={value ?? ''}
            onChange={onChange}
            size="small"
            inputProps={{ maxLength }}
            sx={{ width }}
        />
    );
}

export default function RegraFiscalDialog({ module }) {
    const { state, actions } = module;

    const { dialogAberto, regraEditando, dadosRegra, optionsOperacao, optionsUf, carregandoOpcoes, salvando } = state;

    const { fecharDialog, handleChange, salvarRegra } = actions;

    const publicacaoIncompleta = dadosRegra.integracaoAtivo && (!dadosRegra.cstIcms || !dadosRegra.csosn);

    const dadosInvalidos = !dadosRegra.vigenciaInicio || publicacaoIncompleta;

    const tabContexto = (
        <Stack spacing={2}>
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <Select
                    name="operacaoId"
                    text="Operação"
                    title="Deixe vazio para aplicar a qualquer operação"
                    options={optionsOperacao}
                    handleOnChange={handleChange}
                    value={dadosRegra.operacaoId ?? ''}
                    isEnum={false}
                    notOptNull={false}
                    disabled={carregandoOpcoes}
                    inputWidth="320px"
                />

                <Select
                    name="ufDestinoId"
                    text="UF de destino"
                    title="Deixe vazio para aplicar a qualquer UF"
                    options={optionsUf}
                    handleOnChange={handleChange}
                    value={dadosRegra.ufDestinoId ?? ''}
                    isEnum={false}
                    notOptNull={false}
                    disabled={carregandoOpcoes}
                    inputWidth="240px"
                />

                <Select
                    name="tipoDestinatario"
                    text="Tipo de destinatário"
                    options={TIPOS_DESTINATARIO}
                    handleOnChange={handleChange}
                    value={dadosRegra.tipoDestinatario || 'TODOS'}
                    isEnum={false}
                    notOptNull={true}
                    inputWidth="230px"
                />

                <Select
                    name="consumidorFinal"
                    text="Consumidor final"
                    title="Deixe vazio quando a regra servir para ambos"
                    options={CONSUMIDOR_FINAL}
                    handleOnChange={handleChange}
                    value={dadosRegra.consumidorFinal ?? ''}
                    isEnum={false}
                    notOptNull={false}
                    inputWidth="210px"
                />
            </Stack>

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <TextField
                    name="vigenciaInicio"
                    label="Início da vigência"
                    type="date"
                    value={dadosRegra.vigenciaInicio || ''}
                    onChange={handleChange}
                    size="small"
                    InputLabelProps={{
                        shrink: true,
                    }}
                    required
                />

                <TextField
                    name="vigenciaFim"
                    label="Fim da vigência"
                    type="date"
                    value={dadosRegra.vigenciaFim || ''}
                    onChange={handleChange}
                    size="small"
                    InputLabelProps={{
                        shrink: true,
                    }}
                />

                <TextField
                    label="Origem do cadastro"
                    value={dadosRegra.origemCadastro || 'MANUAL'}
                    size="small"
                    disabled
                    sx={{ width: 210 }}
                />
            </Stack>

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <Checkbox
                    title="Indica que a regra está ativa"
                    text="Ativa"
                    name="ativo"
                    handleOnChange={handleChange}
                    value={dadosRegra.ativo}
                    checked={Boolean(dadosRegra.ativo)}
                />

                <Checkbox
                    title="A regra ainda precisa ser revisada"
                    text="Revisão pendente"
                    name="revisaoPendente"
                    handleOnChange={handleChange}
                    value={dadosRegra.revisaoPendente}
                    checked={Boolean(dadosRegra.revisaoPendente)}
                />

                <Checkbox
                    title="Publica a regra na API"
                    text="Publicada na API"
                    name="integracaoAtivo"
                    handleOnChange={handleChange}
                    value={dadosRegra.integracaoAtivo}
                    checked={Boolean(dadosRegra.integracaoAtivo)}
                />

                <Checkbox
                    title="Compartilha a regra com todas as empresas"
                    text="Todas as empresas"
                    name="integracaoTodasEmpresas"
                    handleOnChange={handleChange}
                    value={dadosRegra.integracaoTodasEmpresas}
                    checked={Boolean(dadosRegra.integracaoTodasEmpresas)}
                />
            </Stack>

            <TextField
                name="observacao"
                label="Observação"
                value={dadosRegra.observacao || ''}
                onChange={handleChange}
                multiline
                minRows={3}
                fullWidth
            />
        </Stack>
    );

    const tabIcms = (
        <Stack spacing={2}>
            {publicacaoIncompleta && <Alert severity="warning">Uma regra publicada precisa possuir CST e CSOSN.</Alert>}

            <Typography variant="subtitle1">ICMS</Typography>

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <CampoCodigo
                    name="cstIcms"
                    label="CST do ICMS"
                    value={dadosRegra.cstIcms}
                    onChange={handleChange}
                    maxLength={3}
                />

                <CampoCodigo name="csosn" label="CSOSN" value={dadosRegra.csosn} onChange={handleChange} maxLength={4} />

                <CampoDecimal
                    name="aliquotaIcms"
                    label="Alíquota do ICMS (%)"
                    value={dadosRegra.aliquotaIcms}
                    onChange={handleChange}
                />

                <CampoDecimal
                    name="reducaoBaseIcms"
                    label="Redução da base (%)"
                    value={dadosRegra.reducaoBaseIcms}
                    onChange={handleChange}
                />
            </Stack>

            <Typography variant="subtitle1">ICMS-ST</Typography>

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <CampoDecimal name="mvaIcmsSt" label="MVA/IVA-ST (%)" value={dadosRegra.mvaIcmsSt} onChange={handleChange} />

                <CampoDecimal
                    name="aliquotaIcmsSt"
                    label="Alíquota ICMS-ST (%)"
                    value={dadosRegra.aliquotaIcmsSt}
                    onChange={handleChange}
                />

                <CampoDecimal
                    name="reducaoBaseIcmsSt"
                    label="Redução base ICMS-ST (%)"
                    value={dadosRegra.reducaoBaseIcmsSt}
                    onChange={handleChange}
                />
            </Stack>

            <Typography variant="subtitle1">FCP</Typography>

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <CampoDecimal name="aliquotaFcp" label="FCP (%)" value={dadosRegra.aliquotaFcp} onChange={handleChange} />

                <CampoDecimal name="aliquotaFcpSt" label="FCP-ST (%)" value={dadosRegra.aliquotaFcpSt} onChange={handleChange} />

                <CampoDecimal
                    name="aliquotaFcpStRetido"
                    label="FCP-ST retido (%)"
                    value={dadosRegra.aliquotaFcpStRetido}
                    onChange={handleChange}
                />
            </Stack>
        </Stack>
    );

    const tabFederais = (
        <Stack spacing={2}>
            <Typography variant="subtitle1">IPI</Typography>

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <CampoCodigo name="cstIpi" label="CST do IPI" value={dadosRegra.cstIpi} onChange={handleChange} maxLength={3} />

                <CampoDecimal
                    name="aliquotaIpi"
                    label="Alíquota do IPI (%)"
                    value={dadosRegra.aliquotaIpi}
                    onChange={handleChange}
                />
            </Stack>

            <Typography variant="subtitle1">PIS</Typography>

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <CampoCodigo name="cstPis" label="CST do PIS" value={dadosRegra.cstPis} onChange={handleChange} maxLength={3} />

                <CampoDecimal
                    name="aliquotaPis"
                    label="Alíquota do PIS (%)"
                    value={dadosRegra.aliquotaPis}
                    onChange={handleChange}
                />

                <CampoDecimal
                    name="valorPautaPis"
                    label="Pauta do PIS"
                    value={dadosRegra.valorPautaPis}
                    onChange={handleChange}
                />
            </Stack>

            <Typography variant="subtitle1">COFINS</Typography>

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <CampoCodigo
                    name="cstCofins"
                    label="CST da COFINS"
                    value={dadosRegra.cstCofins}
                    onChange={handleChange}
                    maxLength={3}
                />

                <CampoDecimal
                    name="aliquotaCofins"
                    label="Alíquota COFINS (%)"
                    value={dadosRegra.aliquotaCofins}
                    onChange={handleChange}
                />

                <CampoDecimal
                    name="valorPautaCofins"
                    label="Pauta da COFINS"
                    value={dadosRegra.valorPautaCofins}
                    onChange={handleChange}
                />

                <CampoCodigo
                    name="unidadePauta"
                    label="Unidade da pauta"
                    value={dadosRegra.unidadePauta}
                    onChange={handleChange}
                    maxLength={10}
                    width={190}
                />
            </Stack>
        </Stack>
    );

    const tabReforma = (
        <Stack spacing={2}>
            <Alert severity="info">
                Informe somente os dados vinculados ao produto. As alíquotas nacionais serão resolvidas pela regra fiscal.
            </Alert>

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <CampoCodigo
                    name="cstIbsCbs"
                    label="CST IBS/CBS"
                    value={dadosRegra.cstIbsCbs}
                    onChange={handleChange}
                    maxLength={10}
                    width={200}
                />

                <CampoCodigo
                    name="cClassTrib"
                    label="Classificação tributária"
                    value={dadosRegra.cClassTrib}
                    onChange={handleChange}
                    maxLength={20}
                    width={280}
                />
            </Stack>
        </Stack>
    );

    const tabsConfig = [
        {
            key: 'contexto',
            label: 'Contexto',
            title: 'Contexto de aplicação',
            panel: tabContexto,
        },
        {
            key: 'icms',
            label: 'ICMS / ST / FCP',
            title: 'Tributação estadual',
            panel: tabIcms,
        },
        {
            key: 'federais',
            label: 'IPI / PIS / COFINS',
            title: 'Tributos federais',
            panel: tabFederais,
        },
        {
            key: 'reforma',
            label: 'IBS / CBS',
            title: 'Reforma tributária',
            panel: tabReforma,
        },
    ];

    return (
        <Dialog open={dialogAberto} onClose={fecharDialog} maxWidth="xl" fullWidth>
            <DialogTitle>{regraEditando ? 'Editar regra fiscal' : 'Cadastrar regra fiscal'}</DialogTitle>

            <DialogContent dividers>
                <SystemTabs tabs={tabsConfig} initialKey="contexto" />
            </DialogContent>

            <DialogActions>
                <Button type="button" onClick={fecharDialog} disabled={salvando}>
                    Cancelar
                </Button>

                <Button
                    type="button"
                    variant="contained"
                    color="success"
                    onClick={salvarRegra}
                    disabled={salvando || dadosInvalidos}
                >
                    {regraEditando ? 'Atualizar regra' : 'Cadastrar regra'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
